import { type FormEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

type SubmissionState = "idle" | "sending" | "sent" | "error";

type ContactMessageResponse = {
  ok: boolean;
  error?: string;
};

type RecaptchaWindow = Window &
  typeof globalThis & {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        parameters: {
          sitekey: string;
          size: "invisible";
          badge: "inline";
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
        },
      ) => number;
      execute: (widgetId: number) => void;
      reset: (widgetId?: number) => void;
    };
  };

const recaptchaScriptId = "google-recaptcha-script";
const clientEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
const defaultRecaptchaSiteKey = "6LeoOOcsAAAAAKeMR2N3KlMmYOiWacqAcsW741ur";

function getRecaptchaSiteKey() {
  const envSiteKey =
    clientEnv?.BUN_PUBLIC_RECAPTCHA_SITE_KEY ||
    clientEnv?.SITE_KEY ||
    (typeof process !== "undefined" ? process.env.BUN_PUBLIC_RECAPTCHA_SITE_KEY || process.env.SITE_KEY : "");
  return envSiteKey?.trim() || defaultRecaptchaSiteKey;
}

type MessageWidgetProps = {
  isHidden?: boolean;
};

export function MessageWidget({ isHidden = false }: MessageWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<SubmissionState>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const recaptchaWidgetId = useRef<number | null>(null);
  const recaptchaResolve = useRef<((token: string) => void) | null>(null);
  const recaptchaReject = useRef<((error: Error) => void) | null>(null);

  const renderRecaptcha = () => {
    const recaptcha = (window as RecaptchaWindow).grecaptcha;
    const recaptchaSiteKey = getRecaptchaSiteKey();

    if (!recaptchaSiteKey) {
      setStatus("error");
      setStatusMessage("Human check is missing a site key. Set BUN_PUBLIC_RECAPTCHA_SITE_KEY in .env.");
      return false;
    }

    if (!recaptcha || !recaptchaRef.current || recaptchaWidgetId.current !== null) {
      return false;
    }

    recaptchaWidgetId.current = recaptcha.render(recaptchaRef.current, {
      sitekey: recaptchaSiteKey,
      size: "invisible",
      badge: "inline",
      callback: token => {
        recaptchaResolve.current?.(token);
        recaptchaResolve.current = null;
        recaptchaReject.current = null;
      },
      "expired-callback": () => {
        recaptchaReject.current?.(new Error("Human check expired. Please try again."));
        recaptchaResolve.current = null;
        recaptchaReject.current = null;
      },
      "error-callback": () => {
        recaptchaReject.current?.(new Error("Human check failed. Please try again."));
        recaptchaResolve.current = null;
        recaptchaReject.current = null;
      },
    });

    return true;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (renderRecaptcha() || document.getElementById(recaptchaScriptId)) {
      return;
    }

    const script = document.createElement("script");
    script.id = recaptchaScriptId;
    script.async = true;
    script.defer = true;
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.onload = () => renderRecaptcha();
    document.body.appendChild(script);
  }, [isOpen]);

  const executeRecaptcha = () =>
    new Promise<string>((resolve, reject) => {
      const recaptcha = (window as RecaptchaWindow).grecaptcha;

      if (!recaptcha || (!renderRecaptcha() && recaptchaWidgetId.current === null)) {
        reject(new Error("Human check is still loading. Please try again."));
        return;
      }

      const widgetId = recaptchaWidgetId.current;
      if (widgetId === null) {
        reject(new Error("Human check is still loading. Please try again."));
        return;
      }

      recaptchaResolve.current = resolve;
      recaptchaReject.current = reject;
      recaptcha.execute(widgetId);
    });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!message.trim()) {
      setStatus("error");
      setStatusMessage("Please enter a message first.");
      return;
    }

    setStatus("sending");
    setStatusMessage("");

    try {
      const recaptchaToken = await executeRecaptcha();
      const response = await fetch("/api/contact/message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          contact,
          message,
          recaptchaToken,
        }),
      });
      const result = (await response.json()) as ContactMessageResponse;

      if (!result.ok) {
        throw new Error(result.error || "Message could not be saved right now.");
      }

      setStatus("sent");
      setStatusMessage("Message sent. We'll get back to you as soon as possible.");
      toast.success("Message sent. We'll get back to you soon.");
      setName("");
      setContact("");
      setMessage("");
      setIsOpen(false);
      (window as RecaptchaWindow).grecaptcha?.reset(recaptchaWidgetId.current ?? undefined);
    } catch (error) {
      const nextMessage = error instanceof Error ? error.message : "Message could not be saved right now.";
      setStatus("error");
      setStatusMessage(nextMessage);
      toast.error(nextMessage);
      (window as RecaptchaWindow).grecaptcha?.reset(recaptchaWidgetId.current ?? undefined);
    }
  };

  return (
    <aside className={`messageWidget${isOpen ? " messageWidget--open" : ""}${isHidden ? " messageWidget--hidden" : ""}`} aria-label="Message George">
      <div className="messageWidget__panel" aria-hidden={!isOpen}>
        <div className="messageWidget__header">
          <span className="messageWidget__eyebrow">Message George</span>
          <button className="messageWidget__close" type="button" aria-label="Close message form" onClick={() => setIsOpen(false)}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <p className="messageWidget__copy">Send a quick booking or music inquiry. We'll get back to you as soon as possible.</p>
        <form className="messageWidget__form" onSubmit={handleSubmit}>
          <label className="messageWidget__field">
            <span>Name</span>
            <input value={name} onChange={event => setName(event.target.value)} name="name" autoComplete="name" placeholder="Your name" />
          </label>
          <label className="messageWidget__field">
            <span>Reply contact</span>
            <input value={contact} onChange={event => setContact(event.target.value)} name="contact" autoComplete="tel" placeholder="Phone or email" />
          </label>
          <label className="messageWidget__field">
            <span>Message</span>
            <textarea value={message} onChange={event => setMessage(event.target.value)} name="message" rows={4} maxLength={1200} placeholder="Your message..." />
          </label>
          <div className="messageWidget__recaptcha" aria-label="Human verification">
            <span className="messageWidget__recaptchaText">Protected by reCAPTCHA</span>
            <div className="messageWidget__recaptchaBadge" ref={recaptchaRef} />
          </div>
          <button className="messageWidget__submit" type="submit" disabled={status === "sending"}>
            {status === "sending" ? "Saving..." : "Send Message"}
          </button>
          {statusMessage ? <p className={`messageWidget__status messageWidget__status--${status}`}>{statusMessage}</p> : null}
        </form>
      </div>

      <button
        className="messageWidget__toggle"
        type="button"
        aria-label={isOpen ? "Close message form" : "Open message form"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(current => !current)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4.75 5.5A2.75 2.75 0 0 1 7.5 2.75h9A2.75 2.75 0 0 1 19.25 5.5v7A2.75 2.75 0 0 1 16.5 15.25h-4.12l-4.25 4.02a.75.75 0 0 1-1.26-.54v-3.48A2.75 2.75 0 0 1 4.75 12.5v-7Zm2.75-1.25A1.25 1.25 0 0 0 6.25 5.5v7c0 .69.56 1.25 1.25 1.25h.12c.41 0 .75.34.75.75v2.49l3.2-3.03a.75.75 0 0 1 .52-.21h4.41c.69 0 1.25-.56 1.25-1.25v-7c0-.69-.56-1.25-1.25-1.25h-9Z" />
          <path d="M8.5 7.5h7v1.5h-7V7.5Zm0 3h4.75V12H8.5v-1.5Z" />
        </svg>
      </button>
    </aside>
  );
}
